import React, { useContext } from 'react';
import * as PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import SystemButton from './SystemButton';
import { storeContext } from '../../context';
import {
  paymentStartPropertyNamesRedirect,
  paymentTypesRedirect
} from '../../../constants/mixpanel';
import {
  capitalize,
  numberToCurrency,
  toQueryStringOldPE
} from '../../../utils/String';

const StyledButton = styled(SystemButton)`
  && {
    a {
      color: ${props => props.theme.palette.button[props.color]?.text};
      &:hover {
        text-decoration: none;
      }
    }
  }
`;

//TODO: These events are not sent to Mixpanel, but as url query params to the Angular Payment App.
//Once patient-client replaces that App, use the standard MixpanelModel, where most properties are already registered

const PaymentButtonRedirect = observer(
  ({
    children,
    onClick,
    paymentButtonType,
    sourceIsMessagesCenter,
    billStatus,
    noteSelectedBills = true,
    ...props
  }) => {
    const {
      payable,
      translations: { currentLanguage },
      providerLinks: { paymentLink }
    } = useContext(storeContext);

    const {
      PP_CONVERT,
      PAY_FULL_PLAN,
      PAYMENT,
      ASSIST_PP,
      ADD_OUTSIDE_BALANCE,
      PP_BRING_CURRENT
    } = paymentTypesRedirect;
    const {
      REMAINING_BALANCE,
      NUMBER_OF_BILLS_OPEN,
      PAYMENT_PAST_DUE,
      BILL_GOING_TO_COLLECTIONS,
      SELECT_BILLS,
      SELECTED_BILL_IDS,
      SELECTED_BILLS_AMOUNT,
      PAYMENT_PLAN_TYPE,
      PAYMENT_PLAN_STATUS,
      BALANCE_ON_PAYMENT_PLAN,
      BALANCE_NOT_ON_PAYMENT_PLAN,
      BILLS_ON_PAYMENT_PLAN,
      MISSED_PAYMENTS,
      BILL_DETAILS_VIEWED,
      PAYMENT_ENTRANCE,
      MESSAGE_CENTER,
      MESSAGE_CENTER_BILL_STATUS,
      BILL_IDS,
      PAYABLE_IDS,
      LANGUAGE,
      CONVERT_PLAN
    } = paymentStartPropertyNamesRedirect;
    const { hasPaymentPlan } = payable;

    const hasSelectBill = () => payable.hasBillSelectable;
    const includeSelectedBills =
      hasSelectBill() && noteSelectedBills && payable.selectableNextPayment;

    const getPaymentLink = () => {
      const getPaymentPlanStatus = status => {
        //transform 'good_standing' into 'Good+Standing';
        return capitalize(status, '_').replace(/_/g, '+');
      };

      //gather required information
      //this is TEMPORARY, most props are already calculated and registered in the TrackingModel
      const mixpanelObj = {
        [PAYMENT_ENTRANCE]: paymentButtonType,
        ...(paymentButtonType === PP_CONVERT && { [CONVERT_PLAN]: true }),
        [BILL_IDS]: payable.billIds,
        [PAYABLE_IDS]: [payable.id],
        [LANGUAGE]: currentLanguage,
        ...(includeSelectedBills && {
          [SELECTED_BILL_IDS]: payable.selectedBillsIds.toString(),
          [SELECTED_BILLS_AMOUNT]: numberToCurrency(
            payable.selectableNextPayment
          )
        }),
        mixpanel_data: {
          [REMAINING_BALANCE]: payable.standingAmount,
          [BILL_GOING_TO_COLLECTIONS]: payable.hasBillBeforeCollection,
          ...(payable.billDetailsViewed && {
            [BILL_DETAILS_VIEWED]: payable.billDetailsViewed
          }),
          [NUMBER_OF_BILLS_OPEN]: payable.totalBills,
          [PAYMENT_PAST_DUE]: payable.hasBillPastDue,
          [SELECT_BILLS]: hasSelectBill(),
          ...(!!sourceIsMessagesCenter && {
            [MESSAGE_CENTER]: !!sourceIsMessagesCenter,
            [MESSAGE_CENTER_BILL_STATUS]: billStatus
          }),
          [PAYMENT_PLAN_TYPE]: hasPaymentPlan
            ? capitalize(payable.paymentPlan.planType)
            : 'None',
          ...(hasPaymentPlan && {
            [PAYMENT_PLAN_STATUS]: getPaymentPlanStatus(
              payable.paymentPlan.status
            ),
            [BALANCE_ON_PAYMENT_PLAN]: payable.paymentPlan.balanceRemaining,
            [BALANCE_NOT_ON_PAYMENT_PLAN]: payable.balanceNotOnPaymentPlan,
            [BILLS_ON_PAYMENT_PLAN]: payable.billsOnPaymentPlan,
            [MISSED_PAYMENTS]: payable.paymentPlan.missedInstallmentsCount
          })
        }
      };

      return `${paymentLink}?${toQueryStringOldPE(mixpanelObj)}`;
    };

    const onPaymentClick = e => {
      e.preventDefault();
      onClick && onClick(e);
      window.location = getPaymentLink();
    };

    const getTestId = () => {
      switch (paymentButtonType) {
        case PAYMENT:
          return 'payment-button-make-a-payment';
        case PAY_FULL_PLAN:
          return 'payment-button-pay-in-full';
        case ASSIST_PP:
          return 'payment-button-view-payment-plan-options';
        case ADD_OUTSIDE_BALANCE:
          return 'payment-button-add-outside-balance';
        case PP_BRING_CURRENT:
          return 'payment-button-bring-account-current';
      }
    };
    return (
      <StyledButton
        {...props}
        onClickCapture={onPaymentClick}
        data-testid={`${getTestId()}-redirect`}
      >
        <a href={getPaymentLink()}>{children}</a>
      </StyledButton>
    );
  }
);

PaymentButtonRedirect.propTypes = {
  ...SystemButton.propTypes,
  ...{
    paymentButtonType: PropTypes.string.isRequired,
    sourceIsMessagesCenter: PropTypes.bool,
    billStatus: PropTypes.string,
    noteSelectedBills: PropTypes.bool
  }
};

export default PaymentButtonRedirect;
